<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  canDeleteContact,
  canViewContacts,
  type Contact,
  type CreateContactInput,
  ContactCreateForm,
  ContactDeleteDialog,
  ContactList,
  useContact,
  useContacts,
} from '@/features/contacts'
import { canForgiveContact, ForgiveDialog, forgivableReceivables } from '@/features/forgiveness'
import { useSession } from '@/app/session'
import { useToasts } from '@/app/toasts'

const { t } = useI18n()
const session = useSession()
const toasts = useToasts()

const canView = computed(() => canViewContacts(session.can))
const canForgive = computed(() => canForgiveContact(session.can))
const canDelete = computed(() => canDeleteContact(session.can))

const contacts = useContacts({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
})

const createErrorMessage = computed(() =>
  contacts.hasCreateError.value
    ? (contacts.createErrorDetail.value ?? t('contacts.createErrorBody'))
    : undefined,
)

const selected = ref<{ contact: Contact; action: 'forgive' | 'delete' }>()
const selectedContact = useContact({
  ledgerId: () => session.activeLedger.value?.id,
  contactId: () => selected.value?.contact.id ?? '',
  enabled: () => selected.value !== undefined,
})
const receivables = computed(() => forgivableReceivables(selectedContact.balances.value))
const forgiveReady = computed(() => selected.value?.action === 'forgive' && !selectedContact.isLoading.value)
const forgiveSubject = computed(() =>
  selected.value === undefined
    ? undefined
    : {
        target: { kind: 'contact' as const, contactId: selected.value.contact.id },
        name: selected.value.contact.name,
        owed: receivables.value,
      },
)

// The list carries no balances: the forgive dialog opens once they load, and only when something is owed.
watch(forgiveReady, (ready) => {
  if (!ready || selected.value === undefined) return
  if (selectedContact.error.value !== null) {
    toasts.push('danger', t('contacts.detailErrorTitle'))
    selected.value = undefined
  } else if (receivables.value.length === 0) {
    toasts.push('neutral', t('contacts.nothingToForgive', { name: selected.value.contact.name }))
    selected.value = undefined
  }
})

function select(contact: Contact, action: 'forgive' | 'delete'): void {
  selectedContact.resetDelete()
  selected.value = { contact, action }
}

function onForgiven(): void {
  selected.value = undefined
  toasts.push('ok', t('contacts.forgiven'))
}

async function remove(): Promise<void> {
  try {
    await selectedContact.deleteContact()
    selected.value = undefined
    toasts.push('ok', t('contacts.deleted'))
  } catch {
    // surfaced through selectedContact.deleteErrorDetail
  }
}

function createContact(input: CreateContactInput): void {
  contacts.create(input).catch(() => {
    // surfaced through contacts.createError; nothing further to do here
  })
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('contacts.title')">
      <template #subtitle>{{ t('contacts.subtitle') }}</template>
      <template #actions>
        <ContactCreateForm
          v-if="canView"
          :is-submitting="contacts.isCreating.value"
          :error-message="createErrorMessage"
          @create="createContact"
        />
      </template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('contacts.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('contacts.noAccess') }}
    </p>

    <ContactList
      v-else
      :contacts="contacts.contacts.value"
      :is-loading="contacts.isLoading.value"
      :error="contacts.error.value"
      :retrying="contacts.retrying.value"
      :can-forgive="canForgive"
      :can-delete="canDelete"
      @retry="contacts.retry()"
      @forgive="(contact) => select(contact, 'forgive')"
      @delete="(contact) => select(contact, 'delete')"
    />

    <ForgiveDialog
      v-if="session.activeLedger.value"
      :open="forgiveReady && receivables.length > 0"
      :ledger-id="session.activeLedger.value.id"
      :subject="forgiveSubject"
      @close="selected = undefined"
      @forgiven="onForgiven"
    />

    <ContactDeleteDialog
      :open="selected?.action === 'delete'"
      :name="selected?.contact.name ?? ''"
      :balances="selectedContact.balances.value"
      :is-deleting="selectedContact.isDeleting.value"
      :failed="selectedContact.hasDeleteError.value"
      :denied="selectedContact.deleteDenied.value"
      :error-detail="selectedContact.deleteErrorDetail.value"
      @close="selected = undefined"
      @confirm="remove"
    />
  </PageShell>
</template>
